import React, { useEffect, useState, createContext, useContext } from 'react';
import { UserData, dolibarrAPI, calculateTrialDates, isTrialExpired, odoo } from '../utils/api';
import { verifyPassword } from '../utils/security';
import { detectCountryFromPhone } from './useCountry';
import { dbUsers } from '../utils/supabase';
interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: UserData) => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<UserData>) => void;
  checkTrialStatus: () => boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('operiens_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        // ── Always refresh from Supabase on mount ──────────────────
        try {
          const freshData = await dbUsers.getByEmail(userData.email);
          if (freshData) {
            const refreshed = {
              ...userData,
              plan: freshData.plan,
              isAccountSuspended: freshData.is_account_suspended,
              isTrialActive: freshData.is_trial_active,
              instanceName: freshData.instance_name || userData.instanceName,
              systemMessage: freshData.system_message || userData.systemMessage
            };
            localStorage.setItem('operiens_user', JSON.stringify(refreshed));
            setUser(refreshed);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.error('Supabase refresh error:', e);
        }
        // ──────────────────────────────────────────────────────────
        // Fallback: use stored data
        if (userData.plan === 'trial' && userData.trialEndDate) {
          const expired = isTrialExpired(userData.trialEndDate);
          if (expired && !userData.isAccountSuspended) {
            userData.isAccountSuspended = true;
            userData.isTrialActive = false;
            localStorage.setItem('operiens_user', JSON.stringify(userData));
          }
        }
        if (!userData.country && userData.phone) {
          userData.country = detectCountryFromPhone(userData.phone);
          localStorage.setItem('operiens_user', JSON.stringify(userData));
          localStorage.setItem('operines_country', userData.country);
        }
        setUser(userData);
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);
  const login = async (email: string, password: string) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (!email || !password) {
          reject(new Error('يرجى إدخال البريد الإلكتروني وكلمة المرور'));
          return;
        }
        // 1. Try Supabase first
        let userData: any = null;
        let source = '';
        try {
          const supabaseUser = await dbUsers.getByEmail(email);
          if (supabaseUser) {
            console.log('✅ User found in Supabase:', supabaseUser.email);
            source = 'supabase';
            userData = {
              name: supabaseUser.name,
              email: supabaseUser.email,
              phone: supabaseUser.phone,
              password: supabaseUser.password,
              plan: supabaseUser.plan as any,
              country: supabaseUser.country,
              brandName: supabaseUser.brand_name,
              instanceName: supabaseUser.instance_name,
              systemMessage: supabaseUser.system_message,
              trialStartDate: supabaseUser.trial_start_date,
              trialEndDate: supabaseUser.trial_end_date,
              isTrialActive: supabaseUser.is_trial_active,
              isAccountSuspended: supabaseUser.is_account_suspended,
              dolibarrThirdPartyId: supabaseUser.dolibarr_id,
              createdAt: supabaseUser.created_at
            };
          }
        } catch (e) {
          console.error('Supabase lookup failed:', e);
        }
        // 2. Fallback to localStorage
        let localUserData: any = null;
        const userKey = `operiens_user_${email.toLowerCase()}`;
        const existingUserData = localStorage.getItem(userKey);
        const existingUserOriginal = localStorage.getItem(`operiens_user_${email}`);
        const existingUser = existingUserData || existingUserOriginal;
        if (existingUser) {
          try {
            localUserData = JSON.parse(existingUser);
          } catch {
            localUserData = null;
          }
        }
        // If no user found anywhere
        if (!userData && !localUserData) {
          reject(new Error('البريد الإلكتروني غير مسجل. يرجى إنشاء حساب جديد.'));
          return;
        }
        // Use Supabase data if available, otherwise localStorage
        if (!userData) {
          userData = localUserData;
          source = 'localStorage';
        }
        // Verify password - try multiple sources
        console.log('🔐 Verifying password from source:', source);
        let isPasswordValid = false;
        // Check if stored hash exists and is valid
        const hasValidHash = userData.password && userData.password.length >= 32;
        if (hasValidHash) {
          try {
            isPasswordValid = await verifyPassword(password, userData.password);
          } catch (e) {
            console.error('Password verification error:', e);
            isPasswordValid = false;
          }
        }
        // If Supabase verification failed, try localStorage as fallback
        if (!isPasswordValid && source === 'supabase' && localUserData?.password) {
          console.log('🔄 Supabase password failed, trying localStorage fallback...');
          const localHashValid = localUserData.password && localUserData.password.length >= 32;
          if (localHashValid) {
            try {
              isPasswordValid = await verifyPassword(password, localUserData.password);
              if (isPasswordValid) {
                console.log('✅ Password verified via localStorage fallback');
                // Sync the correct hash back to Supabase
                dbUsers.update(email, {
                  password: localUserData.password
                } as any).catch(() => {});
              }
            } catch {
              isPasswordValid = false;
            }
          }
        }
        // If localStorage verification failed, try Supabase as fallback
        if (!isPasswordValid && source === 'localStorage' && userData !== localUserData) {
          // Already tried Supabase data above, skip
        }
        // If still not valid, try direct plain-text comparison as last resort
        // (in case password was stored unhashed due to a bug)
        if (!isPasswordValid && userData.password) {
          if (password === userData.password) {
            console.log('⚠️ Password matched as plain text - rehashing and saving');
            isPasswordValid = true;
            // Re-hash and save properly
            const {
              hashPassword
            } = await import('../utils/security');
            const hashedPassword = await hashPassword(password);
            userData.password = hashedPassword;
            // Update both Supabase and localStorage with proper hash
            dbUsers.update(email, {
              password: hashedPassword
            } as any).catch(() => {});
            localStorage.setItem(`operiens_user_${email.toLowerCase()}`, JSON.stringify({
              ...userData,
              password: hashedPassword
            }));
          }
        }
        if (!isPasswordValid) {
          console.log('❌ Password verification failed. Source:', source);
          console.log('❌ Has stored hash:', hasValidHash);
          console.log('❌ Stored hash length:', userData.password?.length || 0);
          // If no valid hash exists at all, suggest password reset
          if (!hasValidHash) {
            reject(new Error('حدث خطأ في بيانات الحساب. يرجى استخدام "نسيت كلمة المرور" لإعادة تعيينها.'));
          } else {
            reject(new Error('كلمة المرور غير صحيحة'));
          }
          return;
        }
        console.log('✅ Password verified successfully from:', source);
        // Merge best data from both sources (prefer Supabase for plan/status, localStorage for local state)
        if (source === 'supabase' && localUserData) {
          // Keep Supabase plan/status but preserve any local-only fields
          userData = {
            ...localUserData,
            ...userData,
            // Supabase is authoritative for these fields
            plan: userData.plan || localUserData.plan,
            isAccountSuspended: userData.isAccountSuspended ?? localUserData.isAccountSuspended,
            isTrialActive: userData.isTrialActive ?? localUserData.isTrialActive
          };
        }
        // Check trial status
        if (userData.plan === 'trial' && userData.trialEndDate) {
          const expired = isTrialExpired(userData.trialEndDate);
          if (expired) {
            userData.isAccountSuspended = true;
            userData.isTrialActive = false;
            // Update Supabase
            dbUsers.update(email, {
              is_account_suspended: true,
              is_trial_active: false
            }).catch(() => {});
          }
        }
        // Detect country from phone if not set
        if (!userData.country && userData.phone) {
          userData.country = detectCountryFromPhone(userData.phone);
        }
        // Update last login in Supabase
        dbUsers.update(email, {
          updated_at: new Date().toISOString()
        } as any).catch(() => {});
        userData.lastLoginAt = new Date().toISOString();
        setUser(userData);
        localStorage.setItem('operiens_user', JSON.stringify(userData));
        localStorage.setItem(`operiens_user_${email.toLowerCase()}`, JSON.stringify(userData));
        if (userData.country) {
          localStorage.setItem('operines_country', userData.country);
        }
        console.log('Login successful:', userData.email);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };
  const register = async (data: UserData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        console.log('=== Starting Registration Process ===');
        // Check if user already exists in Dolibarr
        const existingThirdParty = await dolibarrAPI.getThirdPartyByEmail(data.email);
        let dolibarrId = null;
        if (existingThirdParty) {
          dolibarrId = existingThirdParty.id;
        } else {
          dolibarrId = await dolibarrAPI.createThirdParty(data);
        }
        const trialData = calculateTrialDates();
        const userCountry = data.country || detectCountryFromPhone(data.phone || '');
        const userData: UserData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          password: data.password,
          plan: 'trial',
          country: userCountry,
          brandName: (data as any).brandName || '',
          dolibarrThirdPartyId: dolibarrId || undefined,
          createdAt: new Date().toISOString(),
          ...trialData
        };
        // Create contact in Odoo (non-blocking)
        odoo.createContact(userData).catch(err => {
          console.error('Odoo creation failed (non-blocking):', err);
        });
        // ── Save to Supabase ──────────────────────────────────────────
        const supabasePayload = {
          email: userData.email,
          phone: userData.phone || '',
          name: userData.name,
          brand_name: (userData as any).brandName || '',
          password: userData.password || '',
          plan: userData.plan || 'trial',
          country: userCountry,
          instance_name: userData.instanceName || '',
          system_message: userData.systemMessage || '',
          // ✅ Use null for empty timestamps (not empty string)
          trial_start_date: (userData as any).trialStartDate || null,
          trial_end_date: userData.trialEndDate || null,
          is_trial_active: (userData as any).isTrialActive ?? true,
          is_account_suspended: false,
          dolibarr_id: dolibarrId || ''
        };
        console.log('📤 Sending to Supabase:', {
          ...supabasePayload,
          password: '[HASHED]'
        });
        const supabaseResult = await dbUsers.create(supabasePayload);
        if (supabaseResult) {
          console.log('✅ User saved to Supabase successfully:', supabaseResult.email);
        } else {
          console.error('❌ Supabase save FAILED — check Supabase logs');
        }
        // ─────────────────────────────────────────────────────────────
        // Send to customer webhook (non-blocking)
        const customerWebhookPayload = {
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          plan: userData.plan,
          country: userData.country,
          brandName: (userData as any).brandName || '',
          instanceName: userData.instanceName || '',
          trialStartDate: (userData as any).trialStartDate || '',
          trialEndDate: userData.trialEndDate || '',
          isTrialActive: (userData as any).isTrialActive ?? true,
          dolibarrThirdPartyId: userData.dolibarrThirdPartyId || '',
          createdAt: userData.createdAt,
          registeredAt: new Date().toISOString()
        };
        fetch('https://n8n.operines.me/webhook/customer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(customerWebhookPayload)
        }).catch(() => {
          fetch('https://n8n.operines.me/webhook/customer', {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(customerWebhookPayload)
          }).catch(() => {});
        });
        setUser(userData);
        localStorage.setItem('operiens_user', JSON.stringify(userData));
        localStorage.setItem(`operiens_user_${data.email.toLowerCase()}`, JSON.stringify(userData));
        localStorage.setItem('operines_country', userCountry);
        console.log('✓ Registration completed successfully');
        resolve();
      } catch (error) {
        console.error('Registration error:', error);
        reject(error);
      }
    });
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('operiens_user');
  };
  const updateUser = (data: Partial<UserData>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...data
      };
      setUser(updatedUser);
      localStorage.setItem('operiens_user', JSON.stringify(updatedUser));
      if (updatedUser.email) {
        localStorage.setItem(`operiens_user_${updatedUser.email.toLowerCase()}`, JSON.stringify(updatedUser));
        // Sync to Supabase (non-blocking)
        dbUsers.update(updatedUser.email, {
          instance_name: updatedUser.instanceName || '',
          system_message: updatedUser.systemMessage || '',
          plan: updatedUser.plan || 'trial',
          is_account_suspended: updatedUser.isAccountSuspended ?? false,
          is_trial_active: (updatedUser as any).isTrialActive ?? true,
          brand_name: (updatedUser as any).brandName || '',
          phone: updatedUser.phone || '',
          name: updatedUser.name || ''
        }).catch(() => {});
      }
    }
  };
  const checkTrialStatus = (): boolean => {
    if (!user) return false;
    if (user.plan === 'trial' && user.trialEndDate) {
      const expired = isTrialExpired(user.trialEndDate);
      if (expired && !user.isAccountSuspended) {
        // Suspend account
        const updatedUser = {
          ...user,
          isAccountSuspended: true,
          isTrialActive: false
        };
        setUser(updatedUser);
        localStorage.setItem('operiens_user', JSON.stringify(updatedUser));
        localStorage.setItem(`operiens_user_${updatedUser.email}`, JSON.stringify(updatedUser));
        return false;
      }
      return !expired;
    }
    // If user has a paid plan, they're always active
    return user.plan !== 'trial';
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    checkTrialStatus
  }} data-id="element-735">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}